import { PlusIcon } from '@heroicons/react/24/solid';
import { Building, Eye, EyeOff } from 'lucide-react';
import Page from '../../../components/PageWrapper';
import { useState } from 'react';
import GTable from '../../../components/elements/GTable';
import { Insurer } from '../../../models/insurer/Insurer';
import Button from '../../../components/form-control/Button';
import { useNavigate } from 'react-router-dom';
import { ITableRowAction } from '../../../components/elements/GTable/GTable';
import { capitalizeSentence } from '../../../utils/miscFunctions';
import useRoute from '../../../hooks/routesHook';
import useUserAccount from '../../../hooks/userAccountHook';
import { User } from '../../../models/User';
import useModal from '../../../hooks/modalHook';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import { useInsurer } from '../../../hooks/insurerHook';
import React from 'react';

const Insurers = (): JSX.Element => {
  const page = 'Insurer'
  const { getProfile } = useUserAccount();
  const user = new User(getProfile());
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [searchString, setSearchString] = useState<string>('');
  const [showPerPage, setShowPerPage] = useState<number>(100);
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1


  const getTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [];
    if (user.isInternalOwner()) {
      // if (user.isSuperAdmin()) {
      if (data?.isActive()) {
        actions.push({
          label: 'Unpublish',
          icon: <EyeOff className="h-4 w-4" />,
          callback: () => unPublishInsurer(data),
          data: data
        });
      }

      if (!data?.isActive()) {
        actions.push({
          label: 'Publish',
          icon: <Eye className="h-4 w-4" />,
          callback: () => publishInsurer(data),
          data: data
        });
      }
    }

    return actions;
  };

  const { getInsurers, columns, handlePageChange, publishInsurer, unPublishInsurer } = useInsurer({ getTableRowActions });

 
  const { data: insurersData, isLoading } = useQuery({
      queryKey: ['insurers', countryId, showPerPage],
      queryFn: getInsurers,
      staleTime: 1000 * 10,
      placeholderData: (prev) => prev,
      gcTime: 1000 * 60 * 30 * 5,
      select: (response) => ({
          allInsurers: response?.data,
      }),
  });

  const insurers = React.useMemo(() => {
      return insurersData?.allInsurers?.map((_insurer: any) => new Insurer(_insurer));
  }, [insurersData?.allInsurers]);
  

  const sortedInsurers = insurers ?  insurers?.sort((a: any, b: any) => {
    return a.name.localeCompare(b.name)
  }) : [];

  return (
    <Page title={page}>
      <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg">
        <div className="flex items-center justify-between">
          <div className="">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">{capitalizeSentence(page)}s</h2>
            </div>

            <p className="text-sm text-slate-400 dark:text-darkBg-subText">
              View a list of all {page}s and their details{' '}
            </p>
          </div>

          {/* Add Insurer */}
          <Button
            className=""
            icon={<Building className="h-4 w-4 mr-1" />}
            curved
            bgColor="primary"
            label="Add Insurer"
            onClick={() => navigate(`${getDashboardBase()}/insurers/add-insurer`)}
          />
        </div>

        <div className="overflow-x-auto mt-4">
          <GTable
            columns={columns}
            data={sortedInsurers && sortedInsurers.filter((insurer: Insurer) =>
              insurer?.name?.toLowerCase()?.includes(searchString)
            ) }
            showPagination={false}
            loading={isLoading}
            onSearch={(searchString: string) => setSearchString(searchString)}
            searchPlaceholder={'Search by name...'}
            tableType={page.toLowerCase()}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            onRowClick={(insurer: Insurer) => {
              navigate(`/admin/insurers/${insurer.id}`, { replace: true });
            }}
            classes={'max-h-[400px]'}
            stickyHeader
          />
        </div>
      </div>
      <ModalComponent />
    </Page>
  );
};

export default Insurers;
